import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';

export interface RubricCriterion {
  id: string;
  label: string;
  points: number;
}

interface RubricEditorProps {
  criteria: RubricCriterion[];
  onChange: (criteria: RubricCriterion[]) => void;
}

// Globally-unique criterion id. A length/label seed is non-monotonic after a
// delete (delete-then-add can collide), so use a real uuid with a safe fallback.
const genId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? `crit_${crypto.randomUUID()}`
    : `crit_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e6).toString(36)}`;

/** Persisted alongside the AI-assist config in config.grading. Reordering is by add order (no drag dep). */
export const RubricEditor: React.FC<RubricEditorProps> = ({ criteria, onChange }) => {
  const { t } = useTranslation();

  const update = (id: string, patch: Partial<RubricCriterion>) =>
    onChange(criteria.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const remove = (id: string) => onChange(criteria.filter((c) => c.id !== id));
  const add = () => onChange([...criteria, { id: genId(), label: '', points: 1 }]);

  return (
    <div className="space-y-2">
      {criteria.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('rubric_empty')}</p>
      ) : (
        <div className="space-y-2">
          {criteria.map((c) => (
            <div
              key={c.id}
              className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 focus-within:border-foreground/40"
            >
              <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-muted-foreground/40" aria-hidden />
              <Input
                value={c.label}
                onChange={(e) => update(c.id, { label: e.target.value })}
                placeholder={t('rubric_criterion_placeholder')}
                aria-label={t('rubric_criterion_label')}
                className="h-8 flex-1 border-0 bg-transparent px-0 shadow-none focus-visible:!outline-none focus-visible:ring-0"
              />
              <Input
                type="number"
                min={0}
                value={c.points}
                onChange={(e) => update(c.id, { points: Math.max(0, Number(e.target.value) || 0) })}
                aria-label={t('rubric_criterion_points')}
                className="h-8 w-16 text-right"
              />
              <span className="text-xs text-muted-foreground">{t('points')}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                onClick={() => remove(c.id)}
                aria-label={t('delete')}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="pt-1">
        <Button variant="ghost" size="sm" onClick={add}>
          <Plus className="mr-2 h-4 w-4" />
          {t('rubric_add_criterion')}
        </Button>
      </div>
    </div>
  );
};

export default RubricEditor;
